/**
 * App — providers that must wrap the whole console.
 *
 * ToastProvider sits OUTSIDE the router so a toast raised during navigation (or from a
 * route-level error) still has somewhere to land. The error boundary is outside both,
 * because a provider that throws would otherwise take the page to white.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import ToastProvider from '@/components/Toast';
import { AppRouter } from '@/router';

interface BoundaryState {
  error: Error | null;
}

/**
 * Last-resort boundary. Route-level failures are caught by RouteError in router.tsx;
 * this catches everything above the router (provider construction, the router itself).
 */
class RootErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Never include request payloads here: they can carry recipient emails.
    console.error('[nrd:app] unhandled error', error.message, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="boot-screen">
        <div className="boot-card">
          <h1 style={{ fontSize: 'var(--fs-lg)' }}>The console stopped</h1>
          <p className="muted">
            Something failed outside any single screen. Reloading usually clears it; if it
            does not, the browser console has the stack.
          </p>
          <div className="code-block">{error.message}</div>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload console
          </button>
        </div>
      </div>
    );
  }
}

export function App() {
  return (
    <RootErrorBoundary>
      <ToastProvider>
        <AppRouter />
      </ToastProvider>
    </RootErrorBoundary>
  );
}

export default App;
